import { HttpErrorResponse, HttpParams } from '@angular/common/http';
import { LogicielCaisseAuthService } from './logiciel-caisse-auth.service';

/** IDs backend logiciel-caisse (localStorage / session LC). */
export function requireLcUserStationIds(
  auth: LogicielCaisseAuthService
): { userId: number; stationId: number } {
  const ids = auth.readLcIdsFromLocalStorage();
  if (!ids) {
    throw new Error(
      'Session logiciel caisse incomplète : reconnectez-vous (user_id / station_id manquants).'
    );
  }
  return ids;
}

/** Query `user_id` + `station_id` (couple LC) sur les GET logiciel caisse. */
export function appendLcUserStationParams(
  params: HttpParams,
  auth: LogicielCaisseAuthService
): HttpParams {
  const ids = auth.readLcIdsFromLocalStorage();
  if (!ids) {
    return params;
  }
  return params.set('user_id', String(ids.userId)).set('station_id', String(ids.stationId));
}

/** Date calendaire locale YYYY-MM-DD (évite le décalage UTC de toISOString). */
export function formatLocalDateKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function todayLocalDateKey(): string {
  return formatLocalDateKey(new Date());
}

/** URL app logiciel caisse (hors page login). */
export function isLogicielCaisseAppUrl(url: string): boolean {
  const u = String(url || '');
  if (u.includes('/logiciel-caisse/auth')) {
    return false;
  }
  return u.includes('/logiciel-caisse') || u.includes('/main/parametre');
}

export function lcApiBase(auth: LogicielCaisseAuthService): string {
  return auth.apiBase();
}

export function lcStationId(auth: LogicielCaisseAuthService): number {
  const stationId = auth.getBackStationId();
  if (stationId == null) {
    throw new Error('Session incomplète : reconnectez-vous (station back caisse manquante).');
  }
  return stationId;
}

export function lcHttpError(err: { error?: unknown; status?: number; statusText?: string }, fallback: string): Error {
  const body = err.error as { error?: unknown; message?: string } | string | null;
  if (typeof body === 'object' && body !== null) {
    const apiError = body.error;
    if (typeof apiError === 'string' && apiError.trim()) {
      return new Error(apiError.trim());
    }
    if (typeof body.message === 'string' && body.message.trim()) {
      return new Error(body.message.trim());
    }
  }
  if (typeof body === 'string' && body.trim()) {
    return new Error(body.trim());
  }
  if (err.status === 403) {
    return new Error('Accès refusé pour cette station.');
  }
  if (err.status === 401) {
    return new Error('Session logiciel caisse expirée. Reconnectez-vous.');
  }
  if (err.status === 0) {
    return new Error('Impossible de joindre le serveur logiciel caisse.');
  }
  if (err.status && err.status >= 400) {
    const statusText = String(err.statusText ?? '').trim();
    if (statusText && statusText !== 'Unknown Error' && statusText !== 'OK') {
      return new Error(`${fallback} (${err.status} ${statusText})`);
    }
    return new Error(fallback);
  }
  return new Error(fallback);
}

/** Message d'erreur RxJS (Error, HttpErrorResponse, string, ou réponse HTTP brute). */
export function readObservableError(err: unknown, fallback: string): string {
  if (typeof err === 'function') {
    try {
      return readObservableError((err as () => unknown)(), fallback);
    } catch {
      return fallback;
    }
  }
  if (err instanceof HttpErrorResponse) {
    return lcHttpError(err, fallback).message;
  }
  if (err instanceof Error) {
    const msg = String(err.message ?? '').trim();
    if (msg && !msg.startsWith('Http failure response')) {
      return msg;
    }
    if (msg.startsWith('Http failure response')) {
      return fallback;
    }
  }
  if (typeof err === 'string' && err.trim()) {
    return err.trim();
  }
  if (typeof err === 'object' && err !== null) {
    const raw = err as {
      error?: { error?: string; message?: string } | string;
      message?: string;
      status?: number;
    };
    if (typeof raw.error === 'object' && raw.error !== null) {
      if (typeof raw.error.error === 'string' && raw.error.error.trim()) {
        return raw.error.error.trim();
      }
      if (typeof raw.error.message === 'string' && raw.error.message.trim()) {
        return raw.error.message.trim();
      }
    }
    if (typeof raw.error === 'string' && raw.error.trim()) {
      return raw.error.trim();
    }
    if (raw.message && String(raw.message).trim() && !String(raw.message).startsWith('Http failure response')) {
      return String(raw.message).trim();
    }
    if (raw.status != null) {
      return lcHttpError(raw, fallback).message;
    }
  }
  return fallback;
}

/**
 * Log détaillé en console (debug réception / PATCH transfert LC) + message utilisateur.
 */
export function logLogicielCaisseHttpError(
  context: string,
  err: unknown,
  extra?: Record<string, unknown>
): string {
  const message = readObservableError(err, 'Erreur logiciel caisse');

  if (err instanceof HttpErrorResponse) {
    console.group(`[Logiciel caisse] ${context}`);
    console.error('Message:', message);
    console.error('HTTP', err.status, err.statusText);
    console.error('URL:', err.url);
    console.error('Corps réponse (err.error):', err.error);
    if (extra && Object.keys(extra).length) {
      console.error('Contexte:', extra);
    }
    console.groupEnd();
  } else {
    console.group(`[Logiciel caisse] ${context}`);
    console.error('Message:', message);
    console.error('Erreur brute:', err);
    if (extra && Object.keys(extra).length) {
      console.error('Contexte:', extra);
    }
    console.groupEnd();
  }

  return message;
}
